import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { eur, frDate, EVENT_STATUS, catColor } from "@/lib/constants";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { TrendingUp, Clock, CalendarDays, AlertCircle, ArrowUpRight, LineChart, PieChart, PackageSearch, Undo2 } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { motion } from "framer-motion";
import { Widget, PageHeader } from "@/components/Widget";

function KpiCard({ icon: Icon, label, value, accent, i, to, testid }) {
  const navigate = useNavigate();
  return (
    <motion.button
      data-testid={testid}
      onClick={() => navigate(to)}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: i * 0.06 }}
      className={`group flex items-center gap-4 p-5 text-left transition-colors hover:bg-white/[0.02] ${i ? "lg:border-l lg:border-zinc-800" : ""} ${i % 2 ? "border-l border-zinc-800 lg:border-l" : ""} ${i >= 2 ? "border-t border-zinc-800 lg:border-t-0" : ""}`}
    >
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${accent}`}>
        <Icon className="h-5 w-5" strokeWidth={1.7} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs text-zinc-500">{label}</p>
        <p className="mt-0.5 font-display text-xl font-bold text-white sm:text-2xl">{value}</p>
      </div>
      <ArrowUpRight className="hidden h-4 w-4 text-zinc-600 transition-colors group-hover:text-[#FF6A00] sm:block" />
    </motion.button>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { org } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-zinc-500">Chargement…</div>;

  const checks = [
    ["Nom", org?.name], ["Logo", org?.logo], ["Site web", org?.brand_website],
    ["Email de contact", org?.contact_email], ["Téléphone", org?.phone], ["Adresse", org?.address],
    ["SIRET", org?.siret], ["N° TVA", org?.vat_number],
    ["Modules", org?.modules?.length], ["Types d'événements", org?.event_types?.length],
    ["Politique d'annulation", org?.cancellation_policy],
  ];
  const doneCount = checks.filter(([, v]) => !!v).length;
  const pct = Math.round((doneCount / checks.length) * 100);
  const missing = checks.filter(([, v]) => !v).map(([l]) => l);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <PageHeader title="Tableau de bord" subtitle="Voici l'essentiel de votre activité." />

      <div className="grid grid-cols-2 overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 lg:grid-cols-4" data-testid="dash-kpis">
        <KpiCard i={0} testid="kpi-revenue" to="/app/comptabilite" icon={TrendingUp} label="CA du mois" value={eur(data.revenue_month)} accent="bg-[#FF6A00]/15 text-[#FF6A00]" />
        <KpiCard i={1} testid="kpi-pending-quotes" to="/app/devis" icon={Clock} label="Devis en attente" value={data.pending_quotes} accent="bg-amber-500/15 text-amber-400" />
        <KpiCard i={2} testid="kpi-upcoming" to="/app/calendrier" icon={CalendarDays} label="Événements à venir" value={data.upcoming_count} accent="bg-emerald-500/15 text-emerald-400" />
        <KpiCard i={3} testid="kpi-unpaid" to="/app/devis?filtre=impayees" icon={AlertCircle} label="Factures impayées" value={data.unpaid_invoices} accent="bg-red-500/15 text-red-400" />
      </div>

      {org && pct < 100 && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="profile-completion"
          className="overflow-hidden rounded-2xl border border-[#FF6A00]/30 bg-gradient-to-r from-[#FF6A00]/10 via-[#FF6A00]/[0.04] to-transparent p-5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <h2 className="font-display font-semibold text-white">Profil complété à</h2>
                <span className="font-display text-2xl font-bold text-[#FF6A00]" data-testid="profile-pct">{pct}%</span>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-zinc-800">
                <div className="h-full rounded-full bg-gradient-to-r from-[#FF6A00] to-[#FFD500] transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
              <p className="mt-2 text-sm text-zinc-400">Finalisez les infos laissées vides : <span className="text-zinc-300">{missing.slice(0, 5).join(", ")}{missing.length > 5 ? `… (+${missing.length - 5})` : ""}</span></p>
            </div>
            <Button data-testid="complete-profile-btn" onClick={() => navigate("/onboarding")} className="shrink-0 rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]">
              Compléter mon profil <ArrowUpRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </motion.div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <Widget testid="dash-revenue-widget" icon={LineChart} title="Chiffre d'affaires · 6 derniers mois" to="/app/comptabilite" toLabel="Comptabilité" className="lg:col-span-2">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <AreaChart data={data.chart} margin={{ left: -8, right: 8, top: 8 }}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF6A00" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#FF6A00" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#27272a" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} width={56} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toLocaleString("fr-FR", { maximumFractionDigits: 1 })}k€` : `${v}€`)} />
                <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 12, color: "#fff" }} formatter={(v) => eur(v)} />
                <Area type="monotone" dataKey="revenue" stroke="#FF6A00" strokeWidth={2.5} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Widget>

        <Widget testid="dash-summary-widget" icon={PieChart} title="Synthèse" to="/app/calendrier" toLabel="Événements" bodyClass="divide-y divide-zinc-800">
          {[["CA total confirmé", eur(data.total_revenue), "text-[#FFD500]"], ["Événements", data.events_count, "text-white"], ["À venir", data.upcoming_count, "text-white"]].map(([l, v, c]) => (
            <div key={l} className="flex items-center justify-between px-5 py-4">
              <span className="text-sm text-zinc-500">{l}</span>
              <span className={`font-display text-xl font-bold ${c}`}>{v}</span>
            </div>
          ))}
        </Widget>
      </div>

      {(data.return_reminders?.length > 0 || data.high_demand?.length > 0) && (
        <div className="grid gap-6 lg:grid-cols-2">
          {data.return_reminders?.length > 0 && (
            <Widget testid="return-reminders" icon={Undo2} title="Retours de matériel en attente" to="/app/calendrier" toLabel="Calendrier" bodyClass="p-0">
              <p className="px-5 pt-4 text-xs text-zinc-500">Événements passés : validez l'état des lieux pour les clôturer.</p>
              <ul className="divide-y divide-zinc-800 p-2">
                {data.return_reminders.map((r) => (
                  <li key={r.id}><button data-testid={`return-reminder-${r.id}`} onClick={() => navigate(`/app/evenements/${r.id}`)} className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm transition-colors hover:bg-zinc-800/60">
                    <span className="truncate text-white">{r.title}</span><span className="shrink-0 rounded-md bg-blue-500/10 px-2 py-0.5 text-xs text-blue-300">{r.pending} à rendre</span>
                  </button></li>
                ))}
              </ul>
            </Widget>
          )}
          {data.high_demand?.length > 0 && (
            <Widget testid="high-demand-alert" icon={PackageSearch} title="Matériel très demandé" to="/app/stock" toLabel="Catalogue" bodyClass="p-0">
              <p className="px-5 pt-4 text-xs text-zinc-500">Ces articles tournent beaucoup : envisagez une unité supplémentaire.</p>
              <ul className="space-y-2 p-4">
                {data.high_demand.map((h) => (
                  <li key={h.id} className="text-sm">
                    <div className="flex justify-between"><span className="truncate text-white">{h.name}</span><span className="text-[#FFD500]">{h.utilization}%</span></div>
                    <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-zinc-800"><div className="h-full rounded-full bg-[#FFD500]" style={{ width: `${Math.min(100, h.utilization)}%` }} /></div>
                  </li>
                ))}
              </ul>
            </Widget>
          )}
        </div>
      )}

      <Widget testid="dash-next-events" icon={CalendarDays} title="Prochains événements" to="/app/calendrier" toLabel="Voir le calendrier">
        {data.next_events.length === 0 ? (
          <div className="py-10 text-center">
            <CalendarDays className="mx-auto h-10 w-10 text-zinc-700" />
            <p className="mt-3 text-zinc-500">Aucun événement à venir pour l'instant.</p>
            <Button data-testid="dash-create-event" onClick={() => navigate("/app/calendrier?new=1")} className="mt-4 rounded-full bg-zinc-800 hover:bg-zinc-700">Créer un événement</Button>
          </div>
        ) : (
          <div className="space-y-2">
            {data.next_events.map((e) => (
              <div key={e.id} data-testid={`dash-event-${e.id}`} onClick={() => navigate(`/app/evenements/${e.id}`)}
                className="group flex cursor-pointer items-center gap-4 overflow-hidden rounded-xl border border-[#FF6A00]/25 bg-gradient-to-r from-[#FF6A00]/10 via-[#FF6A00]/[0.04] to-transparent p-4 transition-all hover:border-[#FF6A00]/60 hover:from-[#FF6A00]/15">
                <div className="flex h-12 w-12 flex-col items-center justify-center rounded-xl bg-gradient-to-br from-[#FF6A00] to-[#FFD500] text-center shadow-sm shadow-[#FF6A00]/30">
                  <span className="text-xs font-medium text-black/70">{new Date(e.date).toLocaleDateString("fr-FR", { month: "short" })}</span>
                  <span className="font-display text-lg font-bold text-black">{new Date(e.date).getDate()}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 truncate font-medium text-white"><span className={`h-2 w-2 shrink-0 rounded-full ${catColor(e.category).dot}`} title={catColor(e.category).label} />{e.title}</p>
                  <p className="truncate text-sm text-zinc-400">{e.contact_name || "Sans contact"} · {e.category}</p>
                </div>
                <StatusBadge status={e.status} />
                <ArrowUpRight className="h-4 w-4 text-[#FF6A00] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </div>
            ))}
          </div>
        )}
      </Widget>
    </div>
  );
}
